/**
 * Tests value = base + k * step for an integer k, without changing the value.
 *
 * Allow up to eight floating-point rounding units in the normalized operands,
 * capped at one ten-millionth of an increment so large values cannot expand
 * the tolerance enough to accept a meaningful fraction of a step. Reject
 * overflow and unsafe increment counts, where Number cannot reliably decide
 * alignment. This is deliberately independent of bounds and currency units.
 */
export function isNumberStepAligned(value: number, step: number, base = 0): boolean {
  if (!Number.isFinite(value) || !Number.isFinite(base) || !Number.isFinite(step) || step <= 0) return false;

  const increments = (value - base) / step;
  const nearest = Math.round(increments);
  if (!Number.isSafeInteger(nearest)) return false;

  const magnitude = Math.max(1, Math.abs(value / step), Math.abs(base / step), Math.abs(increments));
  const tolerance = Math.min(1e-7, 8 * Number.EPSILON * magnitude);
  return Math.abs(increments - nearest) <= tolerance;
}
